import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceChargeInvoiceProcessSelectionsModel } from "services/api/cplapi/CPLAPI";
import { IServiceChargeInvoicingService } from "services/ServiceChargeInvoicingService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { ServiceChargeInvoicingSteps } from "./ServiceChargeInvoiceWizardSteps";

type ServiceChargeInvoicingProps = {
  LoadingServiceChargeModel: boolean;
  ServiceChargeInvoice: ServiceChargeInvoiceProcessSelectionsModel;
  CurrentStep: ServiceChargeInvoicingSteps;
  IsBackgroundProcessRunning: boolean;
};

const initialState: ServiceChargeInvoicingProps = {
  LoadingServiceChargeModel: false,
  ServiceChargeInvoice: new ServiceChargeInvoiceProcessSelectionsModel(),
  CurrentStep: ServiceChargeInvoicingSteps.ServiceChargeDateCodes,
  IsBackgroundProcessRunning: false,
};

export const serviceChargeInvoicingSlice = createSlice({
  name: "serviceChargeInvoicing",
  initialState,
  reducers: {
    setServiceChargeInvoiceModel: (state, action) => {
      state.ServiceChargeInvoice = action.payload;
    },
    setCurrentStep: (state, action) => {
      state.CurrentStep = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceChargeInvoiceProcessSelectionModel.pending, (state) => {
        state.LoadingServiceChargeModel = true;
      })
      .addCase(getServiceChargeInvoiceProcessSelectionModel.rejected, (state) => {
        state.LoadingServiceChargeModel = false;
      })
      .addCase(getServiceChargeInvoiceProcessSelectionModel.fulfilled, (state, action) => {
        state.LoadingServiceChargeModel = false;
        state.ServiceChargeInvoice = action.payload;
        state.IsBackgroundProcessRunning = action.payload.isBackgroundProcessRunning ?? false;
        if (action.payload.currentStep === 0) state.CurrentStep = 1;
        else state.CurrentStep = action.payload.currentStep!;
      })
      .addCase(saveServiceChargeInvoiceProcessSelectionModel.pending, (state) => {
        state.LoadingServiceChargeModel = true;
      })
      .addCase(saveServiceChargeInvoiceProcessSelectionModel.rejected, (state) => {
        state.LoadingServiceChargeModel = false;
      })
      .addCase(saveServiceChargeInvoiceProcessSelectionModel.fulfilled, (state) => {
        state.LoadingServiceChargeModel = false;
      });
  },
});

export const getServiceChargeInvoiceProcessSelectionModel = createAsyncThunk(
  "serviceChargeInvoicing/getServiceChargeInvoiceProcessSelectionModel",
  async () => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    return await serviceChargeInvoicingService.resumeProcess();
  }
);

export const saveServiceChargeInvoiceProcessSelectionModel = createAsyncThunk<void, undefined, { state: RootState }>(
  "serviceChargeInvoicing/saveServiceChargeInvoiceProcessSelectionModel",
  async (_, thunkAPI) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    const state = thunkAPI.getState();
    await serviceChargeInvoicingService.saveProcess(state.serviceChargeInvoicing.ServiceChargeInvoice);
  }
);

export const completeServiceChargeInvoicingWizard = createAsyncThunk<void, undefined, { state: RootState }>(
  "serviceChargeInvoicing/completeServiceChargeInvoicingWizard",
  async (_, thunkAPI) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    const state = thunkAPI.getState();
    return await serviceChargeInvoicingService.completeServiceChargeInvoicingWizard(
      state.serviceChargeInvoicing.ServiceChargeInvoice
    );
  }
);

export const { setServiceChargeInvoiceModel, setCurrentStep } = serviceChargeInvoicingSlice.actions;

export const selectServiceChargeInvoice = (state: RootState) => state.serviceChargeInvoicing.ServiceChargeInvoice;
export const selectLoadingServiceChargeInvoice = (state: RootState) =>
  state.serviceChargeInvoicing.LoadingServiceChargeModel;
export const selectServiceChargeInvoiceCurrentStep = (state: RootState) =>
  state.serviceChargeInvoicing.CurrentStep ?? 1;
export const selectIsBackgroundProcessRunning = (state: RootState) =>
  state.serviceChargeInvoicing.IsBackgroundProcessRunning;

export default serviceChargeInvoicingSlice.reducer;
